interface SearchNode<T> {
  state: T;
  parent: SearchNode<T> | null;
  g: number;  // Cost from start to current node
  h: number;  // Estimated cost from current node to goal
  f: number;  // Total cost (g + h)
}

interface AStarConfig<T> {
  // Get neighboring states and their costs from current state
  getNeighbors: (state: T) => { state: T; cost: number }[];
  // Heuristic function estimating cost from current state to goal
  heuristic: (current: T, goal: T) => number;
  // Function to check if current state equals goal state
  equals: (a: T, b: T) => boolean;
  // Optional function to generate unique key for state (for comparison)
  getKey?: (state: T) => string;
}

class AStar<T> {
  private config: AStarConfig<T>;

  constructor(config: AStarConfig<T>) {
      this.config = {
          ...config,
          getKey: config.getKey || ((state: T) => JSON.stringify(state))
      };
  }

  search(start: T, goal: T): { path: T[]; cost: number } | null {
      const openSet: SearchNode<T>[] = [];
      const closedSet = new Set<string>();
      
      // Initialize start node
      const startNode: SearchNode<T> = {
          state: start,
          parent: null,
          g: 0,
          h: this.config.heuristic(start, goal),
          f: 0
      };
      startNode.f = startNode.g + startNode.h;
      openSet.push(startNode);

      console.log('Starting search');
      console.log('Start Node:', startNode);

      while (openSet.length > 0) {
          // Get node with lowest f score
          const current = this.getLowestFScore(openSet);
          console.log('Current Node:', current);
          
          // Check if we reached the goal
          if (this.config.equals(current.state, goal)) {
              console.log('Goal reached:', current);
              return this.reconstructPath(current);
          }

          // Move current node from open to closed set
          this.removeFromOpenSet(openSet, current);
          closedSet.add(this.config.getKey!(current.state));
          console.log('Moved to closed set:', current);

          // Get neighbors
          const neighbors = this.config.getNeighbors(current.state);
          console.log('Neighbors:', neighbors);
          
          for (const neighbor of neighbors) {
              const neighborKey = this.config.getKey!(neighbor.state);
              
              // Skip if neighbor is in closed set
              if (closedSet.has(neighborKey)) {
                  console.log('Neighbor in closed set, skipping:', neighbor);
                  continue;
              }

              // Calculate g score for this path
              const tentativeG = current.g + neighbor.cost;

              // Find if neighbor is already in open set
              const existingNeighbor = this.findInOpenSet(openSet, neighbor.state);

              if (!existingNeighbor) {
                  // New node discovered
                  const neighborNode: SearchNode<T> = {
                      state: neighbor.state,
                      parent: current,
                      g: tentativeG,
                      h: this.config.heuristic(neighbor.state, goal),
                      f: 0
                  };
                  neighborNode.f = neighborNode.g + neighborNode.h;
                  openSet.push(neighborNode);
                  console.log('New neighbor added to open set:', neighborNode);
              } else if (tentativeG < existingNeighbor.g) {
                  // Found a better path to an existing node
                  existingNeighbor.parent = current;
                  existingNeighbor.g = tentativeG;
                  existingNeighbor.f = tentativeG + existingNeighbor.h;
                  console.log('Updated existing neighbor with better path:', existingNeighbor);
              }
          }
      }

      // No path found
      console.log('No path found');
      return null;
  }

  private getLowestFScore(openSet: SearchNode<T>[]): SearchNode<T> {
      return openSet.reduce((min, node) => 
          node.f < min.f ? node : min, openSet[0]);
  }

  private removeFromOpenSet(openSet: SearchNode<T>[], node: SearchNode<T>): void {
      const index = openSet.indexOf(node);
      if (index > -1) {
          openSet.splice(index, 1);
      }
  }

  private findInOpenSet(openSet: SearchNode<T>[], state: T): SearchNode<T> | undefined {
      return openSet.find(node => 
          this.config.equals(node.state, state));
  }

  private reconstructPath(goalNode: SearchNode<T>): { path: T[]; cost: number } {
      const path: T[] = [];
      let current: SearchNode<T> | null = goalNode;
      
      while (current !== null) {
          path.unshift(current.state);
          current = current.parent;
      }

      return {
          path,
          cost: goalNode.g
      };
  }
}

export { AStar, AStarConfig, SearchNode };